import React, { useEffect } from "react";
import { useBrand } from "@/lib/brand";
import { mediaUrl } from "@/lib/api";

/**
 * Favicon — the browser-tab icon, and the home-screen icon, which are not
 * the same picture.
 *
 * The static <link rel="icon"> tags in index.html are Foundation's own
 * default and apply until a tenant's brand supplies `assets.favicon_url`.
 * Those static tags aren't something React's auto-hoisting can dedupe
 * against on its own (same caveat as the description tag in SeoHead), so
 * this manually strips them before rendering the tenant's replacements.
 *
 * A FAVICON IS NOT AN APP ICON. This used to point `apple-touch-icon` at
 * the same file as `rel="icon"`, and an agency's favicon is 32 pixels
 * square: iOS upscales whatever it is given to 180, so every agency that
 * had set one had a blurry smear on the home screen of anybody who added
 * their site. It also deleted any apple-touch-icon whose href differed,
 * which quietly undid the client app's own.
 *
 * So the tab keeps the favicon and the home screen gets the generated app
 * icon (backend/clientapp.py): the right size, the agency's mark on the
 * agency's ground, and the same image the installed client app uses.
 */
export default function Favicon() {
  const brand = useBrand();
  const favicon = brand.assets?.favicon_url;
  const slug = brand.tenant_slug || "";
  const appIcon = mediaUrl(
    `/api/app-icon-180.png${slug ? `?tenant=${encodeURIComponent(slug)}` : ""}`);

  useEffect(() => {
    if (!favicon) return;
    const href = mediaUrl(favicon);
    // Only strip tags that AREN'T the ones we just rendered — removing
    // indiscriminately here would also delete our own <link> the moment
    // React hoists it into <head>, since this effect fires right after.
    const keep = new Set([href, appIcon]);
    document.querySelectorAll('link[rel~="icon"], link[rel="apple-touch-icon"]').forEach((el) => {
      if (!keep.has(el.getAttribute("href"))) el.remove();
    });
  }, [favicon, appIcon]);

  if (!favicon) return null;
  const href = mediaUrl(favicon);
  return (
    <>
      <link rel="icon" href={href} />
      <link rel="apple-touch-icon" href={appIcon} />
    </>
  );
}
